/**
 * axios请求封装
 * get、post
 */
import axios from 'axios';
import store from '@/store';
import router from '@/router';
import { baseURL } from '@/config';
import { getIeVer } from '@/utils';
import request from '@/utils/request';
import Message from '@/utils/message';
import { _loginByUserName } from '@/utils/login';

// IE版本号
const ieVer = getIeVer();

/**
 * 跳转登录页
 */
const toLogin = () => {
  // 第三方系统嵌入
  if (store.state.isIframe) {
    _loginByUserName();
  } else {
    if (router.currentRoute.path === '/home/login') {
      return;
    }
    store.dispatch('resetToken');
    const redirect = router.currentRoute.fullPath;
    const query = redirect !== '/' ? { redirect } : {};
    router.replace({
      path: '/home/login',
      query
    });
  }
};

/**
 * 请求响应失败后错误统一处理
 * @param {Number} status 失败状态码
 * @param {String} message 消息文字
 */
const errorHandler = (status, message) => {
  switch (status) {
    // 未登录
    case 401:
      toLogin();
      break;
    // 权限不足
    case 403:
      // router.push('/403');
      message = '无权限操作，请联系管理员';
      break;
    // 请求不存在
    case 404:
      message = '请求出错，请稍后重试';
      break;
    // 登录失效
    case 405:
      message = '登录失效，请重新登录';
      toLogin();
      break;
    // 请求实体过大
    case 413:
      message = '上传文件过大';
      break;
    // 服务器出错
    case 500:
      router.push('/500');
      break;
    // 错误网关
    case 502:
      message = '错误网关，请稍后重试';
      break;
    // 服务不可用
    case 503:
      message = '服务不可用，请稍后重试';
      break;
    // 网关超时
    case 504:
      message = '网关超时，请稍后重试';
      break;
  }

  if (store.state.isIframe && (status === 401 || status === 405)) {
    message = '页面登录超时，请重新点击菜单进入';
  }

  message && Message.error(message);
};

const http = axios.create({
  baseURL,
  timeout: 60000,
  withCredentials: true
});

// http请求拦截器
http.interceptors.request.use(
  (config) => {
    // 检查是否存在重复请求，若存在则取消已发的请求
    request.remove(config);
    // 把当前请求信息添加到 pendingRequest 对象中
    request.add(config);

    const token = store.state.token;
    token && (config.headers.Authorization = token);
    return config;
  },
  (error) => {
    // 超时
    if (error.message.includes('timeout')) {
      Message.error('请求超时，请稍后重试');
    }
    return Promise.reject(error);
  }
);

// http响应拦截器
http.interceptors.response.use(
  (res) => {
    // 从 pendingRequest 对象中移除请求
    request.remove(res.config);

    if (res.status === 200) {
      // 数据格式为blob且IE10以下
      if (res.config.responseType === 'blob' && ieVer && ieVer < 10) {
        Message.error('您的浏览器不支持文件流导出');
      }
      // 统一错误提示
      if (res.data) {
        const { code, message } = res.data;
        if (code !== 0 && message) {
          Message.error(message);
        }
      }
      const { authorization } = res.headers;
      if (authorization) {
        res.data.authorization = authorization;
      }
      return Promise.resolve(res.data);
    } else {
      return Promise.reject(res);
    }
  },
  (error) => {
    // 从 pendingRequest 对象中移除请求
    request.remove(error.config || {});
    if (axios.isCancel(error)) {
      console.log('已取消的重复请求：' + error.message);
    }
    const { response } = error;
    if (response) {
      const { status, data } = response;
      errorHandler(status, data.message);
      return Promise.reject(response).catch(() => { });
    }

    // 网络未连接
    if (!window.navigator.onLine) {
      Message.error('网络未连接，请检查网络后重试');
    }

    // 超时
    if (error.message.includes('timeout')) {
      Message.error('响应超时，请稍后重试');
    }

    return Promise.reject(error).catch(() => { });
  }
);

export default http;

